import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const fmtM = (n) => `${((n || 0) / 1e6).toLocaleString("it-IT", { maximumFractionDigits: 1 })} mln`;

export default function AdminPrizesImport() {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [season, setSeason] = useState("");
    const [busy, setBusy] = useState("");
    const [err, setErr] = useState("");
    const [openTeam, setOpenTeam] = useState(null);
    const [batches, setBatches] = useState([]);
    const [done, setDone] = useState(null);
    const [recatMsg, setRecatMsg] = useState("");

    const loadBatches = async () => {
        try {
            const { data } = await api.get("/admin/prizes/batches");
            setBatches(data.batches || []);
        } catch { /* ignore */ }
    };
    useEffect(() => { loadBatches(); }, []);

    const onFile = (e) => {
        setFile(e.target.files?.[0] || null);
        setPreview(null); setDone(null); setErr("");
    };

    const runPreview = async () => {
        if (!file) { setErr("Seleziona prima il file Excel."); return; }
        setBusy("preview"); setErr(""); setPreview(null); setDone(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/prizes/preview", fd, { headers: { "Content-Type": undefined } });
            setPreview(data);
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally { setBusy(""); }
    };

    const apply = async () => {
        if (!season.trim()) { setErr("Inserisci l'etichetta stagione (es. 2024/25)."); return; }
        if (!window.confirm(`Applicare i premi?\n\nTotale ${fmtM(preview.grand_total)} accreditati a ${preview.teams_count} squadre.\nLa cassa OP passerà da ${fmtM(preview.op_balance_before)} a ${fmtM(preview.op_balance_after)}.\n\nL'operazione è reversibile dall'elenco batch.`)) return;
        setBusy("apply"); setErr("");
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("season_label", season.trim());
            const { data } = await api.post("/admin/prizes/apply", fd, { headers: { "Content-Type": undefined } });
            setDone(data); setPreview(null); setFile(null);
            await loadBatches();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally { setBusy(""); }
    };

    const revert = async (b) => {
        if (!window.confirm(`Annullare il batch "${b.season_label}"?\n\nVerranno scalati ${fmtM(b.grand_total)} dai budget delle squadre e rimossi i movimenti dalla cassa OP.`)) return;
        setBusy("revert-" + b.batch_id); setErr("");
        try {
            await api.post("/admin/prizes/revert", { batch_id: b.batch_id });
            await loadBatches();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally { setBusy(""); }
    };

    const rename = async (b) => {
        const label = window.prompt(
            `Rinomina l'etichetta stagione del batch.\n\nUsa la STESSA etichetta su più batch della stessa stagione per unirli in un'unica voce nell'Albo e nella classifica.`,
            b.season_label || "");
        if (label === null) return;
        const trimmed = label.trim();
        if (!trimmed) { setErr("Etichetta stagione vuota."); return; }
        if (trimmed === b.season_label) return;
        setBusy("rename-" + b.batch_id); setErr("");
        try {
            await api.post("/admin/prizes/rename", { batch_id: b.batch_id, season_label: trimmed });
            await loadBatches();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally { setBusy(""); }
    };

    const blocked = preview && (preview.unmatched?.length > 0 || preview.unmapped_motivi?.length > 0);

    const recategorize = async () => {
        if (!window.confirm("Normalizza le categorie dei premi già importati.\n\nI motivi liberi ora riconosciuti (es. \"Fair Play\") vengono uniti alla voce nota corrispondente (es. \"Premio Fair Play\"). Operazione sicura e ripetibile.")) return;
        setBusy("recat"); setErr(""); setRecatMsg("");
        try {
            const { data } = await api.post("/admin/prizes/recategorize");
            const merged = Object.entries(data.merged || {});
            setRecatMsg(merged.length
                ? `✓ Unite ${data.movements_updated} voci di cassa e ${data.lines_updated} righe albo — ${merged.map(([k, v]) => `${k} (${fmtM(v)})`).join(", ")}.`
                : "Nessuna voce libera da normalizzare: è già tutto a posto.");
            await loadBatches();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally { setBusy(""); }
    };

    return (
        <div data-testid="admin-prizes-page">
            <div className="op-label" style={{ color: "rgb(var(--tc-9d7cff))" }}>// IMPORTA PREMI</div>
            <h2 className="op-heading text-3xl mt-2 mb-2">Importa premi stagione</h2>
            <p className="op-mono text-xs text-t8f95a3 mb-5 max-w-3xl leading-relaxed">
                Carica l'Excel (colonne: <span className="text-tffffff">motivo · squadra · importo</span>). Anteprima
                squadra-per-squadra e impatto sulla cassa OP, poi <span className="text-t7ce38b">Applica</span>.
                Lato squadre: una riga di movimento per ogni premio. Lato cassa OP: voci aggregate per categoria.
                Il <span className="text-tffffff">motivo</span> nella prima colonna è <span className="text-tffffff">libero</span>:
                i motivi noti (Champions, Coppe, Individuali…) vengono riconosciuti da soli; qualsiasi altro testo
                (es. <span className="text-tffffff">Fair Play</span>, <span className="text-tffffff">Spese stadio</span>)
                diventa una voce di cassa OP con lo stesso identico nome. Operazione <span className="text-t7ce38b">reversibile</span>.
            </p>

            {/* Normalizza categorie già importate (unisce voci libere ora riconosciute, es. Fair Play) */}
            <div className="op-card p-3 mb-4 flex flex-wrap items-center gap-3">
                <button onClick={recategorize} disabled={busy === "recat"}
                    className="op-mono text-xs uppercase tracking-wider px-4 py-2 border border-t2bb6ff text-t2bb6ff hover:bg-t2bb6ff/10 transition-colors disabled:opacity-40"
                    data-testid="prizes-recategorize-btn">
                    {busy === "recat" ? "Normalizzo…" : "↻ Normalizza categorie premi"}
                </button>
                <span className="op-mono text-[11px] text-t8f95a3 flex-1 min-w-[200px]">
                    {recatMsg
                        ? <span className="text-t7ce38b">{recatMsg}</span>
                        : "Unisce i premi liberi già importati alle voci note (es. \"Fair Play\" → \"Premio Fair Play\")."}
                </span>
            </div>

            {err && <div className="border border-tff3b30 bg-tff3b30/10 text-tff3b30 text-xs p-2 mb-4" data-testid="prizes-err">{err}</div>}

            {/* Upload + anteprima */}
            <div className="op-card p-4 mb-5">
                <div className="flex flex-wrap items-center gap-3">
                    <input type="file" accept=".xlsx" onChange={onFile} className="op-mono text-xs text-tc9ccd3" data-testid="prizes-file-input" />
                    <button onClick={runPreview} disabled={busy === "preview" || !file}
                        className="op-mono text-xs uppercase tracking-wider px-4 py-2 border border-t9d7cff text-t9d7cff hover:bg-t9d7cff/10 transition-colors disabled:opacity-40"
                        data-testid="prizes-preview-btn">
                        {busy === "preview" ? "Analisi…" : "Anteprima"}
                    </button>
                </div>
            </div>

            {done && (
                <div className="op-card p-4 mb-5 border border-t7ce38b/40" data-testid="prizes-done">
                    <div className="text-t7ce38b op-mono text-sm">✓ Premi applicati: {fmtM(done.grand_total)} a {done.teams_credited} squadre · {done.lines} movimenti · cassa OP ora {fmtM(done.op_balance_after)}</div>
                </div>
            )}

            {preview && (
                <div className="space-y-5 mb-6" data-testid="prizes-preview">
                    {/* Riepilogo */}
                    <div className="grid sm:grid-cols-3 gap-3">
                        <div className="op-card p-3">
                            <div className="op-label text-[10px]">Totale premi</div>
                            <div className="text-tffffff text-lg font-bold" data-testid="prizes-grand-total">{fmtM(preview.grand_total)}</div>
                            <div className="op-mono text-[10px] text-t5c6370">{preview.rows_count} righe · {preview.teams_count} squadre</div>
                        </div>
                        <div className="op-card p-3">
                            <div className="op-label text-[10px]">Cassa OP prima</div>
                            <div className="text-tffffff text-lg font-bold">{fmtM(preview.op_balance_before)}</div>
                        </div>
                        <div className="op-card p-3">
                            <div className="op-label text-[10px]">Cassa OP dopo</div>
                            <div className={`text-lg font-bold ${preview.op_balance_after < 0 ? "text-tff7a1a" : "text-t7ce38b"}`}>{fmtM(preview.op_balance_after)}</div>
                        </div>
                    </div>

                    {/* Avvisi bloccanti */}
                    {preview.unmatched?.length > 0 && (
                        <div className="border border-tff3b30 bg-tff3b30/10 text-tff3b30 text-xs p-3" data-testid="prizes-unmatched">
                            ⚠️ Squadre non riconosciute (correggi il file): {preview.unmatched.map((u) => `${u.name} (${fmtM(u.amount)})`).join(", ")}
                        </div>
                    )}
                    {preview.unmapped_motivi?.length > 0 && (
                        <div className="border border-tffb020 bg-tffb020/10 text-tffb020 text-xs p-3">
                            ⚠️ Motivi non categorizzati: {preview.unmapped_motivi.join(", ")}
                        </div>
                    )}

                    {/* Categorie cassa OP */}
                    <div className="op-card overflow-hidden">
                        <div className="px-3 py-2 op-label text-[10px] text-t9d7cff border-b border-t22252a">// USCITE CASSA OP — PER CATEGORIA</div>
                        <table className="w-full text-sm">
                            <tbody>
                                {preview.op_categories.map((c) => (
                                    <tr key={c.key} className="border-b border-t1a1d24" data-testid={`prizes-cat-${c.key}`}>
                                        <td className="py-2 px-3 op-mono text-tc9ccd3">{c.label}</td>
                                        <td className="py-2 px-3 text-right op-mono text-tff7a1a">−{fmtM(c.total)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Squadre */}
                    <div className="op-card overflow-hidden">
                        <div className="px-3 py-2 op-label text-[10px] text-t7ce38b border-b border-t22252a">// ACCREDITI SQUADRE · {preview.teams_count}</div>
                        <div className="divide-y divide-t1a1d24">
                            {preview.teams.map((t) => (
                                <div key={t.team_id} data-testid={`prizes-team-${t.team_id}`}>
                                    <button onClick={() => setOpenTeam(openTeam === t.team_id ? null : t.team_id)}
                                        className="w-full flex justify-between items-center px-3 py-2 hover:bg-t0f1115 text-left">
                                        <span className="op-mono text-sm text-tffffff">{t.team_name} <span className="text-t5c6370 text-[10px]">({t.lines.length} voci)</span></span>
                                        <span className="op-mono text-sm text-t7ce38b">+{fmtM(t.total)}</span>
                                    </button>
                                    {openTeam === t.team_id && (
                                        <div className="px-3 pb-2 bg-t0a0b0d">
                                            {t.lines.map((ln, i) => (
                                                <div key={i} className="flex justify-between op-mono text-[10px] text-t8f95a3 py-0.5">
                                                    <span className="truncate pr-2">{ln.voce}</span>
                                                    <span className="text-t7ce38b shrink-0">+{fmtM(ln.amount)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Applica */}
                    <div className="op-card p-4 flex flex-wrap items-center gap-3">
                        <input type="text" value={season} onChange={(e) => setSeason(e.target.value)} placeholder="Etichetta stagione (es. 2024/25)"
                            className="bg-t0a0b0d border border-t22252a focus:border-t7ce38b outline-none text-tffffff text-sm px-3 py-2 op-mono flex-1 min-w-[200px]"
                            data-testid="prizes-season-input" />
                        <button onClick={apply} disabled={busy === "apply" || blocked || !season.trim()}
                            className="op-btn border border-t7ce38b text-t7ce38b hover:bg-t7ce38b hover:text-black px-5 py-2 disabled:opacity-40 transition-colors"
                            data-testid="prizes-apply-btn">
                            {busy === "apply" ? "Applico…" : `Applica ${fmtM(preview.grand_total)}`}
                        </button>
                        {blocked && <span className="op-mono text-[10px] text-tff3b30">Risolvi gli avvisi prima di applicare.</span>}
                    </div>
                </div>
            )}

            {/* Batch applicati */}
            {batches.length > 0 && (
                <div className="op-card overflow-hidden" data-testid="prizes-batches">
                    <div className="px-3 py-2 op-label text-[10px] text-t9d7cff border-b border-t22252a">// BATCH APPLICATI</div>
                    <table className="w-full text-sm">
                        <tbody>
                            {batches.map((b) => (
                                <tr key={b.batch_id} className="border-b border-t1a1d24" data-testid={`prizes-batch-${b.batch_id}`}>
                                    <td className="py-2 px-3 op-mono text-tffffff">{b.season_label}</td>
                                    <td className="py-2 px-3 op-mono text-t8f95a3">{fmtM(b.grand_total)} · {b.teams_count} sq · {b.lines_count} voci</td>
                                    <td className="py-2 px-3 op-mono text-[10px] text-t5c6370">{(b.created_at || "").slice(0, 10)}</td>
                                    <td className="py-2 px-3 text-right">
                                        <button onClick={() => rename(b)} disabled={busy === "rename-" + b.batch_id}
                                            className="op-mono text-[10px] uppercase tracking-wider px-3 py-1 mr-2 border border-t9d7cff text-t9d7cff hover:bg-t9d7cff hover:text-tffffff transition-colors disabled:opacity-40"
                                            data-testid={`prizes-rename-${b.batch_id}`}>
                                            {busy === "rename-" + b.batch_id ? "..." : "Rinomina stagione"}
                                        </button>
                                        <button onClick={() => revert(b)} disabled={busy === "revert-" + b.batch_id}
                                            className="op-mono text-[10px] uppercase tracking-wider px-3 py-1 border border-tff3b30 text-tff3b30 hover:bg-tff3b30 hover:text-tffffff transition-colors disabled:opacity-40"
                                            data-testid={`prizes-revert-${b.batch_id}`}>
                                            {busy === "revert-" + b.batch_id ? "..." : "Annulla"}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
